import React, { useState, useEffect, useRef } from 'react';
import { getRedditClient } from '../services/redditClient.js';
import { parseKeywordQuery, validateQuery, getQueryExamples, formatQueryForDisplay } from '../utils/queryParser.js';

function SubredditManagement({ onConfigChange, onProfileChange, redditClientReady }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [subredditConfigs, setSubredditConfigs] = useState([]);
  
  // Profile management state
  const [profiles, setProfiles] = useState([]);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [selectedProfile, setSelectedProfile] = useState(null); // Profile being viewed/configured
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [newProfileDescription, setNewProfileDescription] = useState('');
  const [newConfig, setNewConfig] = useState({
    subreddit: '',
    sortType: 'hot',
    timeframe: null,
    keywords: '',
    configType: 'regular' // 'regular' or 'keyword'
  });
  const [showExamples, setShowExamples] = useState(false);
  const [queryValidation, setQueryValidation] = useState({ isValid: true, error: null });
  const [parsedQuery, setParsedQuery] = useState('');
  const [editQueryValidation, setEditQueryValidation] = useState({ isValid: true, error: null });
  const [editParsedQuery, setEditParsedQuery] = useState('');
  const [editingConfigId, setEditingConfigId] = useState(null);
  const [editForm, setEditForm] = useState({
    subreddit: '',
    sortType: 'hot',
    timeframe: null,
    keywords: '',
    configType: 'regular'
  });

  // Export/Import state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportData, setExportData] = useState('');
  const [showImportForm, setShowImportForm] = useState(false);
  const [importData, setImportData] = useState('');
  const [importProfileName, setImportProfileName] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  // Profile name editing state
  const [editingProfileId, setEditingProfileId] = useState(null);
  const [editingProfileName, setEditingProfileName] = useState('');

  // Available sort types for regular feeds
  const regularSortTypes = [
    { value: 'hot', label: 'Hot' },
    { value: 'new', label: 'New' },
    { value: 'rising', label: 'Rising' },
    { value: 'top', label: 'Top' }
  ];

  // Available sort types for keyword search
  const searchSortTypes = [
    { value: 'relevance', label: 'Relevance', description: 'Best matches' },
    { value: 'hot', label: 'Hot', description: 'Trending posts' },
    { value: 'top', label: 'Top', description: 'Highest scoring' },
    { value: 'new', label: 'New', description: 'Most recent' },
    { value: 'comments', label: 'Comments', description: 'Most discussed' }
  ];

  // Get current sort types based on config type
  const getCurrentSortTypes = () => {
    return newConfig.configType === 'keyword' ? searchSortTypes : regularSortTypes;
  };

  // Available timeframes for 'top' sort
  const timeframes = [
    { value: 'hour', label: 'Hour' },
    { value: 'day', label: 'Day' },
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'year', label: 'Year' },
    { value: 'all', label: 'All Time' }
  ];

  // Load profiles and configurations when Reddit client is ready
  useEffect(() => {
    if (redditClientReady) {
      loadProfiles();
      loadConfigurations();
    }
  }, [redditClientReady]);

  // Set selected profile to current profile initially
  useEffect(() => {
    if (currentProfile && !selectedProfile) {
      setSelectedProfile(currentProfile);
    }
  }, [currentProfile, selectedProfile]);

  // Validate and parse query when keywords change
  useEffect(() => {
    if (newConfig.keywords.trim()) {
      const validation = validateQuery(newConfig.keywords);
      setQueryValidation(validation);
      
      if (validation.isValid) {
        const parsed = parseKeywordQuery(newConfig.keywords);
        setParsedQuery(parsed);
      } else {
        setParsedQuery('');
      }
    } else {
      setQueryValidation({ isValid: true, error: null });
      setParsedQuery('');
    }
  }, [newConfig.keywords]);

  // Validate and parse query when edit form keywords change
  useEffect(() => {
    if (editForm.keywords && editForm.keywords.trim()) {
      const validation = validateQuery(editForm.keywords);
      setEditQueryValidation(validation);
      
      if (validation.isValid) {
        const parsed = parseKeywordQuery(editForm.keywords);
        setEditParsedQuery(parsed);
      } else {
        setEditParsedQuery('');
      }
    } else {
      setEditQueryValidation({ isValid: true, error: null });
      setEditParsedQuery('');
    }
  }, [editForm.keywords]);

  const loadConfigurations = (profileToLoad = null) => {
    try {
      const redditClient = getRedditClient();
      
      if (profileToLoad) {
        // Load configurations for a specific profile without switching to it
        const configs = profileToLoad.configuration?.subredditConfigs || [];
        setSubredditConfigs(configs);
        console.log(`Loaded ${configs.length} subreddit configurations for profile: ${profileToLoad.name}`);
      } else {
        // Load configurations for current profile
        const configs = redditClient.getSubredditConfigs();
        setSubredditConfigs(configs);
        console.log(`Loaded ${configs.length} subreddit configurations for current profile`);
      }
    } catch (error) {
      console.error('Error loading subreddit configurations:', error);
      // Retry after a short delay if Reddit client isn't ready yet
      setTimeout(() => {
        try {
          const redditClient = getRedditClient();
          const configs = redditClient.getSubredditConfigs();
          setSubredditConfigs(configs);
          console.log(`Loaded ${configs.length} subreddit configurations (retry)`);
        } catch (retryError) {
          console.error('Error loading subreddit configurations on retry:', retryError);
        }
      }, 100);
    }
  };

  const handleRemoveConfig = (configId) => {
    try {
      const redditClient = getRedditClient();
      redditClient.removeSubredditConfig(configId);
      loadConfigurations();
      autoSaveToProfile(); // Auto-save to current profile
      onConfigChange?.();
    } catch (error) {
      console.error('Error removing configuration:', error);
    }
  };

  const handleAddConfig = () => {
    if (!newConfig.subreddit.trim()) {
      alert('Please enter a subreddit name.');
      return;
    }

    try {
      const redditClient = getRedditClient();
      
      redditClient.addSubredditConfig(
        newConfig.subreddit.trim(),
        newConfig.sortType,
        newConfig.timeframe
      );
      
      // Reset form
      setNewConfig({
        subreddit: '',
        sortType: 'hot',
        timeframe: null
      });
      
      loadConfigurations();
      onConfigChange?.();
    } catch (error) {
      console.error('Error adding configuration:', error);
      alert('Error adding configuration. Please try again.');
    }
  };

  const handleStartEdit = (config) => {
    setEditingConfigId(config.id);
    setEditForm({
      subreddit: config.subreddit,
      sortType: config.sortType,
      timeframe: config.timeframe,
      keywords: config.keywords || '',
      configType: config.keywords ? 'keyword' : 'regular'
    });
  };

  const handleCancelEdit = () => {
    setEditingConfigId(null);
    setEditForm({
      subreddit: '',
      sortType: 'hot',
      timeframe: null,
      keywords: '',
      configType: 'regular'
    });
  };

  const handleSaveEdit = () => {
    if (!editForm.subreddit.trim()) {
      alert('Please enter a subreddit name.');
      return;
    }

    if (editForm.configType === 'keyword') {
      if (!editForm.keywords.trim()) {
        alert('Please enter keywords to search for.');
        return;
      }
      
      if (!editQueryValidation.isValid) {
        alert(`Invalid query: ${editQueryValidation.error}`);
        return;
      }
    }

    try {
      const redditClient = getRedditClient();
      
      if (editForm.configType === 'keyword') {
        const finalQuery = parseKeywordQuery(editForm.keywords);
        redditClient.updateSubredditConfig(
          editingConfigId,
          editForm.subreddit.trim(),
          editForm.sortType,
          editForm.timeframe,
          finalQuery
        );
      } else {
        redditClient.updateSubredditConfig(
          editingConfigId,
          editForm.subreddit.trim(),
          editForm.sortType,
          editForm.timeframe
        );
      }
      
      setEditingConfigId(null);
      setEditForm({
        subreddit: '',
        sortType: 'hot',
        timeframe: null,
        keywords: '',
        configType: 'regular'
      });
      
      loadConfigurations();
      autoSaveToProfile(); // Auto-save to current profile
      onConfigChange?.();
    } catch (error) {
      console.error('Error updating configuration:', error);
      alert('Error updating configuration. Please try again.');
    }
  };

  const handleUnifiedAddConfig = () => {
    if (!newConfig.subreddit.trim()) {
      alert('Please enter a subreddit name.');
      return;
    }

    if (newConfig.configType === 'keyword') {
      if (!newConfig.keywords.trim()) {
        alert('Please enter keywords to search for.');
        return;
      }
      
      if (!queryValidation.isValid) {
        alert(`Invalid query: ${queryValidation.error}`);
        return;
      }
    }

    try {
      const redditClient = getRedditClient();
      
      if (newConfig.configType === 'keyword') {
        const finalQuery = parseKeywordQuery(newConfig.keywords);
        redditClient.addSubredditConfig(
          newConfig.subreddit.trim(),
          newConfig.sortType,
          newConfig.timeframe,
          finalQuery
        );
      } else {
        redditClient.addSubredditConfig(
          newConfig.subreddit.trim(),
          newConfig.sortType,
          newConfig.timeframe
        );
      }
      
      // Reset form
      setNewConfig({
        subreddit: '',
        sortType: newConfig.configType === 'keyword' ? 'relevance' : 'hot',
        timeframe: null,
        keywords: '',
        configType: newConfig.configType // Keep the same config type
      });
      
      loadConfigurations();
      autoSaveToProfile(); // Auto-save to current profile
      onConfigChange?.();
    } catch (error) {
      console.error('Error adding configuration:', error);
      alert('Error adding configuration. Please try again.');
    }
  };

  const handleKeywordConfigChange = (keywordConfig) => {
    try {
      const redditClient = getRedditClient();
      
      redditClient.addSubredditConfig(
        keywordConfig.subreddit,
        keywordConfig.sortType,
        keywordConfig.timeframe,
        keywordConfig.keywords
      );
      
      loadConfigurations();
      onConfigChange?.();
    } catch (error) {
      console.error('Error adding keyword configuration:', error);
      alert('Error adding keyword configuration. Please try again.');
    }
  };

  const handleResetToDefaults = () => {
    const confirmReset = window.confirm(
      'Are you sure you want to reset all subreddit configurations to defaults? This will remove all custom configurations and restore the original picture-based subreddits.'
    );
    
    if (confirmReset) {
      try {
        const redditClient = getRedditClient();
        redditClient.resetToDefaultConfigs();
        loadConfigurations();
        autoSaveToProfile(); // Auto-save to current profile
        onConfigChange?.();
        alert('Subreddit configurations have been reset to defaults.');
      } catch (error) {
        console.error('Error resetting to defaults:', error);
        alert('Error resetting configurations. Please try again.');
      }
    }
  };

  const getSortTypeLabel = (sortType) => {
    // Try to find in regular sort types first, then search sort types
    const regularSort = regularSortTypes.find(s => s.value === sortType);
    if (regularSort) return regularSort.label;
    
    const searchSort = searchSortTypes.find(s => s.value === sortType);
    if (searchSort) return searchSort.label;
    
    return sortType;
  };

  const getTimeframeLabel = (timeframe) => {
    return timeframes.find(t => t.value === timeframe)?.label || timeframe;
  };

  // Profile management functions
  const loadProfiles = () => {
    if (!redditClientReady) return;
    
    try {
      const redditClient = getRedditClient();
      const allProfiles = redditClient.getProfiles();
      const current = redditClient.getCurrentProfile();
      setProfiles(allProfiles);
      setCurrentProfile(current);
    } catch (error) {
      console.error('Error loading profiles:', error);
    }
  };

  const handleProfileView = (profile) => {
    // Only show the profile's configuration, don't switch active profile
    setSelectedProfile(profile);
    loadConfigurations(profile);
  };

  const handleProfileActivate = async (profileId) => {
    if (!redditClientReady) return;
    
    try {
      const redditClient = getRedditClient();
      const result = redditClient.loadProfile(profileId);
      if (result) {
        setCurrentProfile(result.profile);
        setSelectedProfile(result.profile); // Update selected to match current
        
        // Reload configurations for the new profile
        loadConfigurations();
        
        // Notify parent components of the profile change
        onProfileChange?.(result.profile, result.uiPreferences);
        onConfigChange?.();
        
        console.log(`Activated profile: ${result.profile.name}`);
      }
    } catch (error) {
      console.error('Error activating profile:', error);
      alert('Error activating profile. Please try again.');
    }
  };

  const handleCreateProfile = () => {
    if (!redditClientReady || !newProfileName.trim()) {
      alert('Please enter a profile name.');
      return;
    }

    try {
      const redditClient = getRedditClient();
      const profileId = redditClient.createProfile(newProfileName.trim(), newProfileDescription.trim());
      
      // Reset form
      setNewProfileName('');
      setNewProfileDescription('');
      setShowCreateForm(false);
      
      // Reload profiles and activate the new one
      loadProfiles();
      handleProfileActivate(profileId);
    } catch (error) {
      console.error('Error creating profile:', error);
      alert('Error creating profile. Please try again.');
    }
  };

  const handleDeleteProfile = (profileId, profileName) => {
    if (!redditClientReady) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete the profile "${profileName}"? This action cannot be undone.`
    );

    if (confirmDelete) {
      try {
        const redditClient = getRedditClient();
        const success = redditClient.deleteProfile(profileId);
        if (success) {
          loadProfiles();
          loadConfigurations(); // Reload configurations since current might have changed
          onConfigChange?.(); // Notify parent of configuration change
          console.log(`Deleted profile: ${profileName}`);
        }
      } catch (error) {
        console.error('Error deleting profile:', error);
        alert('Error deleting profile. Please try again.');
      }
    }
  };

  const handleDuplicateProfile = (profileId, profileName) => {
    if (!redditClientReady) return;
    
    const newName = prompt(`Enter a name for the duplicate of "${profileName}":`, `${profileName} Copy`);
    if (newName && newName.trim()) {
      try {
        const redditClient = getRedditClient();
        const duplicateId = redditClient.duplicateProfile(profileId, newName.trim());
        if (duplicateId) {
          loadProfiles();
          console.log(`Duplicated profile: ${profileName} -> ${newName}`);
        }
      } catch (error) {
        console.error('Error duplicating profile:', error);
        alert('Error duplicating profile. Please try again.');
      }
    }
  };

  // Profile name editing functions
  const handleStartEditProfileName = (profile) => {
    setEditingProfileId(profile.id);
    setEditingProfileName(profile.name);
  };

  const handleCancelEditProfileName = () => {
    setEditingProfileId(null);
    setEditingProfileName('');
  };

  const handleSaveProfileName = () => {
    if (!editingProfileName.trim()) {
      alert('Please enter a profile name.');
      return;
    }

    try {
      const redditClient = getRedditClient();
      
      // Find the profile to get its current description
      const profile = profiles.find(p => p.id === editingProfileId);
      if (!profile) {
        throw new Error('Profile not found');
      }
      
      // Use editProfile method with current description
      redditClient.editProfile(editingProfileId, editingProfileName.trim(), profile.description || '');
      
      // Reset editing state
      setEditingProfileId(null);
      setEditingProfileName('');
      
      // Reload profiles to reflect the change
      loadProfiles();
      
      console.log(`Updated profile name to: ${editingProfileName.trim()}`);
    } catch (error) {
      console.error('Error updating profile name:', error);
      alert('Error updating profile name. Please try again.');
    }
  };


  // Auto-save current configuration to profile
  const autoSaveToProfile = () => {
    if (!redditClientReady || !currentProfile) return;
    
    try {
      const redditClient = getRedditClient();
      redditClient.updateProfile(currentProfile.id);
      console.log(`Auto-saved configuration to profile: ${currentProfile.name}`);
    } catch (error) {
      console.error('Error auto-saving to profile:', error);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const formatTimeAgo = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return formatDate(timestamp);
  };

  // Utility function to truncate long keyword strings
  const truncateKeywords = (keywords, maxLength = 120) => {
    if (!keywords || keywords.length <= maxLength) return keywords;
    return keywords.substring(0, maxLength) + '...';
  };

  // Export profile functionality
  const handleExportProfile = (profile) => {
    try {
      const redditClient = getRedditClient();
      const exportData = redditClient.exportProfile(profile.id);
      const base64Data = btoa(JSON.stringify(exportData));
      
      // Create and show export modal
      setExportData(base64Data);
      setShowExportModal(true);
    } catch (error) {
      console.error('Error exporting profile:', error);
      alert('Error exporting profile. Please try again.');
    }
  };

  // Import profile functionality
  const handleImportProfile = () => {
    if (!importProfileName.trim()) {
      alert('Please enter a profile name.');
      return;
    }

    if (!importData.trim()) {
      alert('Please enter profile data to import.');
      return;
    }

    try {
      // Decode base64 and parse JSON
      const decodedData = atob(importData.trim());
      const profileData = JSON.parse(decodedData);
      
      // Validate basic structure
      if (!profileData.configuration) {
        throw new Error('Invalid profile data structure');
      }

      const redditClient = getRedditClient();
      // Use the custom profile name instead of the one from imported data
      const newProfileId = redditClient.importProfile(profileData, importProfileName.trim());
      
      // Reset import form and reload profiles
      setImportData('');
      setImportProfileName('');
      setShowImportForm(false);
      loadProfiles();
      
      // Optionally activate the imported profile
      handleProfileActivate(newProfileId);
      
      alert(`Profile "${importProfileName.trim()}" imported successfully!`);
    } catch (error) {
      console.error('Error importing profile:', error);
      if (error.message.includes('Invalid character')) {
        alert('Invalid base64 data. Please check the imported data.');
      } else if (error.message.includes('JSON')) {
        alert('Invalid profile data format. Please check the imported data.');
      } else {
        alert('Error importing profile: ' + error.message);
      }
    }
  };

  // Copy to clipboard functionality
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  return (
    <div className="subreddit-management">
      <div className="subreddit-management-header">
        <div className="header-main" onClick={() => setIsExpanded(!isExpanded)}>
          <h2 className="subreddit-management-title">
            Subreddit Management
            <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
          </h2>
        </div>
        <div className="header-actions">
          {/* Profile Selector */}
          <div className="header-profile-selector">
            <label className="profile-selector-label">Current Profile:</label>
            <select
              className="profile-selector-dropdown"
              value={currentProfile?.id || ''}
              onChange={(e) => {
                if (e.target.value) {
                  handleProfileActivate(e.target.value);
                }
              }}
            >
              {profiles.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="subreddit-management-content">
          {/* Left Pane - Profile Selection */}
          <div className="profile-pane">
            <div className="profile-section">
              <div className="profile-section-header">
                <h3 className="profile-section-title">Configuration Profiles</h3>
              </div>
              
              {/* Profile List */}
              <div className="profile-list">
                {profiles.length === 0 ? (
                  <div className="no-profiles">
                    <div className="empty-state">
                      <div className="empty-icon">📁</div>
                      <h4>No Profiles</h4>
                      <p>Create your first configuration profile to get started.</p>
                    </div>
                  </div>
                ) : (
                  profiles.map((profile) => (
                    <div
                      key={profile.id}
                      className={`profile-list-item ${currentProfile?.id === profile.id ? 'active' : ''} ${selectedProfile?.id === profile.id ? 'selected' : ''}`}
                    >
                      {editingProfileId === profile.id ? (
                        // Profile name editing mode
                        <div className="profile-item-edit">
                          <div className="profile-name-edit-form">
                            <input
                              type="text"
                              value={editingProfileName}
                              onChange={(e) => setEditingProfileName(e.target.value)}
                              className="profile-name-edit-input"
                              maxLength={50}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveProfileName();
                                } else if (e.key === 'Escape') {
                                  handleCancelEditProfileName();
                                }
                              }}
                            />
                            <div className="profile-name-edit-actions">
                              <button
                                className="profile-name-save-btn"
                                onClick={handleSaveProfileName}
                                disabled={!editingProfileName.trim()}
                                title="Save profile name"
                              >
                                ✓
                              </button>
                              <button
                                className="profile-name-cancel-btn"
                                onClick={handleCancelEditProfileName}
                                title="Cancel editing"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                          <div className="profile-item-meta">
                            <span className="profile-item-count">
                              {profile.configuration.subredditConfigs.length} subreddits
                            </span>
                            <span className="profile-item-time">
                              {formatTimeAgo(profile.lastUsed)}
                            </span>
                          </div>
                          {profile.description && (
                            <div className="profile-item-description">{profile.description}</div>
                          )}
                        </div>
                      ) : (
                        // Normal display mode
                        <>
                          <div 
                            className="profile-item-info"
                            onClick={() => handleProfileView(profile)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="profile-item-name-container">
                              <div className="profile-item-name">
                                {profile.name}
                              </div>
                              <button
                                className="profile-name-edit-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleStartEditProfileName(profile);
                                }}
                                title="Edit profile name"
                              >
                                ✏️
                              </button>
                            </div>
                            <div className="profile-item-meta">
                              <span className="profile-item-count">
                                {profile.configuration.subredditConfigs.length} subreddits
                              </span>
                              <span className="profile-item-time">
                                {formatTimeAgo(profile.lastUsed)}
                              </span>
                            </div>
                            {profile.description && (
                              <div className="profile-item-description">{profile.description}</div>
                            )}
                          </div>
                          <div className="profile-item-actions">
                            <button
                              className="profile-item-action-btn export"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExportProfile(profile);
                              }}
                              title="Export profile"
                            >
                              📤
                            </button>
                            <button
                              className="profile-item-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDuplicateProfile(profile.id, profile.name);
                              }}
                              title="Duplicate profile"
                            >
                              📋
                            </button>
                            <button
                              className="profile-item-action-btn delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteProfile(profile.id, profile.name);
                              }}
                              title="Delete profile"
                            >
                              🗑️
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Import Button and Form */}
              <div className="import-profile-section">
                <button
                  className="create-profile-btn"
                  onClick={() => setShowImportForm(!showImportForm)}
                  title="Import profile from base64 data"
                >
                  📥 Import Profile
                </button>

                {/* Import Form */}
                {showImportForm && (
                  <div className="import-profile-form">
                    <input
                      type="text"
                      placeholder="Profile name (required)"
                      value={importProfileName}
                      onChange={(e) => setImportProfileName(e.target.value)}
                      className="profile-name-input"
                      maxLength={50}
                      required
                    />
                    <textarea
                      placeholder="Paste base64 profile data here..."
                      value={importData}
                      onChange={(e) => setImportData(e.target.value)}
                      className="import-data-input"
                      rows={4}
                    />
                    <div className="import-form-actions">
                      <button
                        className="import-confirm-btn"
                        onClick={handleImportProfile}
                        disabled={!importData.trim() || !importProfileName.trim()}
                      >
                        Import Profile
                      </button>
                      <button
                        className="import-cancel-btn"
                        onClick={() => {
                          setShowImportForm(false);
                          setImportData('');
                          setImportProfileName('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Create New Profile */}
              <div className="create-profile-section">
                {!showCreateForm ? (
                  <button
                    className="create-profile-btn"
                    onClick={() => setShowCreateForm(true)}
                  >
                    ➕ Create New Profile
                  </button>
                ) : (
                  <div className="create-profile-form">
                    <input
                      type="text"
                      placeholder="Profile name"
                      value={newProfileName}
                      onChange={(e) => setNewProfileName(e.target.value)}
                      className="profile-name-input"
                      maxLength={50}
                    />
                    <input
                      type="text"
                      placeholder="Description (optional)"
                      value={newProfileDescription}
                      onChange={(e) => setNewProfileDescription(e.target.value)}
                      className="profile-description-input"
                      maxLength={100}
                    />
                    <div className="create-form-actions">
                      <button
                        className="create-confirm-btn"
                        onClick={handleCreateProfile}
                        disabled={!newProfileName.trim()}
                      >
                        Create
                      </button>
                      <button
                        className="create-cancel-btn"
                        onClick={() => {
                          setShowCreateForm(false);
                          setNewProfileName('');
                          setNewProfileDescription('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Pane - Subreddit Management */}
          <div className="subreddit-pane">
            {/* Current Configurations Section */}
            <div className="management-section">
              <div className="section-header">
                <h3 className="section-title">Configurations</h3>
                <div className="section-actions">
                  <span className="config-count">{subredditConfigs.length} configurations</span>
                  <button
                    className="reset-defaults-btn"
                    onClick={handleResetToDefaults}
                    title="Reset to default subreddit configurations"
                  >
                    🔄 Reset to Defaults
                  </button>
                </div>
              </div>
              <div className="config-list">
                {subredditConfigs.map((config) => (
                  <div key={config.id} className="config-item">
                    {editingConfigId === config.id ? (
                      // Edit form
                      <div className="edit-config-form">
                        <div className="edit-form-header">
                          <h4>Edit Configuration</h4>
                          <div className="edit-form-actions">
                            <button
                              className="save-edit-btn"
                              onClick={handleSaveEdit}
                            >
                              Save
                            </button>
                            <button
                              className="cancel-edit-btn"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                        
                        <div className="edit-form-content">
                          <div className="form-group">
                            <label htmlFor="edit-subreddit">Subreddit:</label>
                            <input
                              id="edit-subreddit"
                              type="text"
                              value={editForm.subreddit}
                              onChange={(e) => setEditForm(prev => ({ ...prev, subreddit: e.target.value }))}
                              className="subreddit-name-input"
                              placeholder="e.g., cats, funny, aww"
                            />
                          </div>

                          {/* Keywords Input (only for keyword configurations) */}
                          {editForm.configType === 'keyword' && (
                            <div className="form-group">
                              <label htmlFor="edit-keywords" className="form-label">
                                Keywords
                              </label>
                              <input
                                id="edit-keywords"
                                type="text"
                                className={`form-input ${!editQueryValidation.isValid ? 'form-input-error' : ''}`}
                                placeholder="e.g., cats AND dogs, funny OR memes"
                                value={editForm.keywords}
                                onChange={(e) => setEditForm(prev => ({ ...prev, keywords: e.target.value }))}
                              />
                              
                              {/* Query Validation */}
                              {!editQueryValidation.isValid && (
                                <div className="form-error">
                                  {editQueryValidation.error}
                                </div>
                              )}
                              
                              {/* Parsed Query Preview */}
                              {editParsedQuery && editQueryValidation.isValid && editParsedQuery !== editForm.keywords && (
                                <div className="query-preview">
                                  <span className="query-preview-label">Parsed as:</span>
                                  <span className="query-preview-text">{editParsedQuery}</span>
                                </div>
                              )}
                            </div>
                          )}

                          <div className="form-group">
                            <label htmlFor="edit-sort-type">Sort Type:</label>
                            <select
                              id="edit-sort-type"
                              value={editForm.sortType}
                              onChange={(e) => setEditForm(prev => ({ 
                                ...prev, 
                                sortType: e.target.value,
                                timeframe: e.target.value === 'top' ? 'day' : null
                              }))}
                              className="sort-type-select"
                            >
                              {(editForm.configType === 'keyword' ? searchSortTypes : regularSortTypes).map(sort => (
                                <option key={sort.value} value={sort.value}>
                                  {sort.label}{sort.description ? ` - ${sort.description}` : ''}
                                </option>
                              ))}
                            </select>
                          </div>

                          {editForm.sortType === 'top' && (
                            <div className="form-group">
                              <label htmlFor="edit-timeframe">Timeframe:</label>
                              <select
                                id="edit-timeframe"
                                value={editForm.timeframe || 'day'}
                                onChange={(e) => setEditForm(prev => ({ ...prev, timeframe: e.target.value }))}
                                className="timeframe-select"
                              >
                                {timeframes.map(time => (
                                  <option key={time.value} value={time.value}>
                                    {time.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      // Normal display
                      <>
                        <div className="config-header">
                          <div className="config-info">
                            <div className="config-main-info">
                              <div className="config-main-content">
                                <span 
                                  className="config-subreddit"
                                  title={`r/${config.subreddit}`}
                                >
                                  r/{config.subreddit}
                                </span>
                                <span className="config-sort">
                                  {config.keywords ? '🔍 ' : ''}
                                  {getSortTypeLabel(config.sortType)}
                                  {config.timeframe && ` (${getTimeframeLabel(config.timeframe)})`}
                                </span>
                              </div>
                              <div className="config-actions">
                                {selectedProfile?.id === currentProfile?.id && (
                                  <>
                                    <button
                                      className="edit-config-btn"
                                      onClick={() => handleStartEdit(config)}
                                      title="Edit configuration"
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      className="remove-config-btn"
                                      onClick={() => handleRemoveConfig(config.id)}
                                      title="Remove configuration"
                                    >
                                      ✕
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                            {config.keywords && (
                              <div className="config-keywords-row">
                                <span 
                                  className="config-keywords"
                                  title={`Keywords: "${config.keywords}"`}
                                >
                                  Keywords: "{truncateKeywords(config.keywords)}"
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
                {subredditConfigs.length === 0 && (
                  <div className="no-configs">No configurations found</div>
                )}
              </div>
            </div>

            {/* Add New Configuration Section - Only for active profile */}
            {selectedProfile?.id === currentProfile?.id && (
              <div className="management-section">
                <h3 className="section-title">Add New Configuration</h3>
              <div className="add-config-form">
                {/* Segmented Control */}
                <div className="form-group">
                  <div className="segmented-control">
                    <button
                      type="button"
                      className={`segment-button ${newConfig.configType === 'regular' ? 'active' : ''}`}
                      onClick={() => setNewConfig(prev => ({ 
                        ...prev, 
                        configType: 'regular',
                        sortType: 'hot',
                        keywords: '',
                        timeframe: null
                      }))}
                    >
                      Regular Feed
                    </button>
                    <button
                      type="button"
                      className={`segment-button ${newConfig.configType === 'keyword' ? 'active' : ''}`}
                      onClick={() => setNewConfig(prev => ({ 
                        ...prev, 
                        configType: 'keyword',
                        sortType: 'relevance',
                        timeframe: null
                      }))}
                    >
                      Keyword Search
                    </button>
                  </div>
                </div>

                {/* Subreddit Input */}
                <div className="form-group">
                  <label htmlFor="new-subreddit">Subreddit:</label>
                  <input
                    id="new-subreddit"
                    type="text"
                    placeholder="e.g., cats, funny, programming"
                    value={newConfig.subreddit}
                    onChange={(e) => setNewConfig(prev => ({ ...prev, subreddit: e.target.value }))}
                    className="form-input"
                  />
                </div>

                {/* Keywords Input (only for keyword search) */}
                {newConfig.configType === 'keyword' && (
                  <div className="form-group">
                    <label htmlFor="new-keywords" className="form-label">
                      Keywords
                      <button
                        type="button"
                        className="help-button"
                        onClick={() => setShowExamples(!showExamples)}
                        title="Show query examples"
                      >
                        ?
                      </button>
                    </label>
                    <input
                      id="new-keywords"
                      type="text"
                      className={`form-input ${!queryValidation.isValid ? 'form-input-error' : ''}`}
                      placeholder="e.g., cats AND dogs, funny OR memes"
                      value={newConfig.keywords}
                      onChange={(e) => setNewConfig(prev => ({ ...prev, keywords: e.target.value }))}
                    />
                    
                    {/* Query Validation */}
                    {!queryValidation.isValid && (
                      <div className="form-error">
                        {queryValidation.error}
                      </div>
                    )}
                    
                    {/* Parsed Query Preview */}
                    {parsedQuery && queryValidation.isValid && parsedQuery !== newConfig.keywords && (
                      <div className="query-preview">
                        <span className="query-preview-label">Parsed as:</span>
                        <span className="query-preview-text">{parsedQuery}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Sort Type */}
                <div className="form-group">
                  <label htmlFor="new-sort-type">Sort Results By:</label>
                  <select
                    id="new-sort-type"
                    value={newConfig.sortType}
                    onChange={(e) => setNewConfig(prev => ({ 
                      ...prev, 
                      sortType: e.target.value,
                      timeframe: e.target.value === 'top' ? 'day' : null
                    }))}
                    className="form-select"
                  >
                    {getCurrentSortTypes().map(sort => (
                      <option key={sort.value} value={sort.value}>
                        {sort.label}{sort.description ? ` - ${sort.description}` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Timeframe (only for 'top' sort) */}
                {newConfig.sortType === 'top' && (
                  <div className="form-group">
                    <label htmlFor="new-timeframe">Time Period:</label>
                    <select
                      id="new-timeframe"
                      value={newConfig.timeframe || 'day'}
                      onChange={(e) => setNewConfig(prev => ({ ...prev, timeframe: e.target.value }))}
                      className="form-select"
                    >
                      {timeframes.map(time => (
                        <option key={time.value} value={time.value}>
                          {time.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Add Button */}
                <div className="form-group">
                  <button
                    type="button"
                    className="add-config-button"
                    onClick={handleUnifiedAddConfig}
                    disabled={!newConfig.subreddit.trim() || 
                      (newConfig.configType === 'keyword' && (!newConfig.keywords.trim() || !queryValidation.isValid))}
                  >
                    Add Configuration
                  </button>
                </div>
              </div>

              {/* Query Examples (only for keyword search) */}
              {newConfig.configType === 'keyword' && showExamples && (
                <div className="query-examples">
                  <h4 className="examples-title">Query Examples</h4>
                  <div className="examples-list">
                    {getQueryExamples().map((example, index) => (
                      <div key={index} className="example-item">
                        <button
                          type="button"
                          className="example-button"
                          onClick={() => {
                            setNewConfig(prev => ({ ...prev, keywords: example.input }));
                            setShowExamples(false);
                          }}
                        >
                          <code className="example-input">{example.input}</code>
                          <span className="example-arrow">→</span>
                          <code className="example-output">{example.output}</code>
                        </button>
                        <p className="example-description">{example.description}</p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="query-syntax-help">
                    <h5>Query Syntax</h5>
                    <ul>
                      <li><strong>AND</strong>: Both terms must be present (default for spaces)</li>
                      <li><strong>OR</strong>: Either term can be present</li>
                      <li><strong>NOT</strong>: Exclude posts with this term</li>
                      <li><strong>"quotes"</strong>: Search for exact phrases</li>
                      <li><strong>(parentheses)</strong>: Group terms for complex queries</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
            )}
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Export Profile</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowExportModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p>Copy the base64 data below to share or backup this profile:</p>
              <textarea
                className="export-data-display"
                value={exportData}
                readOnly
                rows={8}
                onClick={(e) => e.target.select()}
              />
              <div className="modal-actions">
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(exportData)}
                >
                  {copySuccess ? '✓ Copied!' : '📋 Copy to Clipboard'}
                </button>
                <button
                  className="modal-cancel-btn"
                  onClick={() => setShowExportModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SubredditManagement;
